import {ApplicationConfig, inject, isDevMode, PLATFORM_ID, provideZoneChangeDetection, REQUEST} from '@angular/core';
import {provideRouter} from '@angular/router';
import {MAT_FORM_FIELD_DEFAULT_OPTIONS} from "@angular/material/form-field";
import {routes} from './module/app.routes';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {provideHttpClient, withFetch, withInterceptors} from "@angular/common/http";
import {MAT_SNACK_BAR_DEFAULT_OPTIONS} from "@angular/material/snack-bar";
import {provideTranslateService} from "@ngx-translate/core";
import {provideTranslateHttpLoader} from "@ngx-translate/http-loader";
import {ValidationService} from "./util/service/validation/validation-service";
import {HeaderInterceptor} from "./network/header/header.interceptor";
import {LoadingInterceptor} from "./network/loading.interceptor";
import {LoadingService} from "./util/service/loading.service";
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from "@angular/material/core";
import {MaterialPersianDateAdapter, PERSIAN_DATE_FORMATS} from "./util/material.persian.date.adapter";
import {CustomPaginatorConfiguration} from "./util/service/custom.paginator.configuration";
import {MatPaginatorIntl} from "@angular/material/paginator";
import {provideServiceWorker} from '@angular/service-worker';
import {provideClientHydration, withEventReplay} from '@angular/platform-browser';
import {isPlatformServer} from '@angular/common';
import {environment} from '../environments/environment';
import {SsrCookieService} from 'ngx-cookie-service-ssr';
import {AuthenticationService} from './util/service/authentication.service';
import {DeviceService} from './util/service/device.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({eventCoalescing: true}),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(withFetch(), withInterceptors([LoadingInterceptor, HeaderInterceptor])),
    ValidationService,
    provideTranslateService({
      lang: 'fa',
      fallbackLang: 'fa',
      loader: provideTranslateHttpLoader({
        prefix: 'i18n/',
        suffix: '.json'
      })
    }),
    SsrCookieService,
    AuthenticationService,
    DeviceService,
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {duration: 2500}
    }, {
      provide: DateAdapter,
      useClass: MaterialPersianDateAdapter,
      deps: [MAT_DATE_LOCALE]
    }, {
      provide: MAT_DATE_FORMATS,
      useValue: PERSIAN_DATE_FORMATS
    }, {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {appearance: 'outline'}
    }, {
      provide: MatPaginatorIntl,
      useClass: CustomPaginatorConfiguration,
    },
    LoadingService,
    provideServiceWorker('custom-ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideClientHydration(withEventReplay()),
    initializeApp()
  ]
};

export function initializeApp() {
  return {
    provide: 'ENVIRONMENT',
    useFactory: () => {
      const platformId = inject(PLATFORM_ID);
      const request = inject(REQUEST, {optional: true});
      let defaultDomain = 'https://food-shop/';
      let xDomain;
      if (isDevMode()) {
        xDomain = defaultDomain;
      } else {
        if (isPlatformServer(platformId) && request) {
          xDomain = (request.headers.get('x-domain') ? `${request.headers.get('x-domain')}/` : defaultDomain) as string;
        } else {
          xDomain = `${window.location.protocol}//${window.location.host}/`;
        }
      }
      if (xDomain) {
        return {
          fileManagerUrl: xDomain,
          authenticationUrl: xDomain,
          baseUrl: xDomain,
          generalUrl: xDomain
        };
      }
      return environment;
    }
  };
}
